import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, LogIn, ExternalLink, User, Heart } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

const AdminLogin = () => {
  const [username, setUsername] = useState('loran');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    document.title = 'Connexion admin — Loranrix';
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(username.trim(), password);

    if (result.success) {
      toast({
        title: 'Connexion réussie',
        description: 'Bienvenue dans l’espace d’administration.',
      });
      navigate('/admin/dashboard');
    } else {
      toast({
        title: 'Échec de connexion',
        description: result.error || 'Identifiant ou mot de passe incorrect.',
        variant: 'destructive',
      });
    }

    setLoading(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#090b16] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090b16] flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-24 w-80 h-80 bg-pink-500/15 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-24 w-80 h-80 bg-purple-600/15 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="relative w-full max-w-md"
      >
        <div className="bg-gradient-to-br from-[#1a1024] to-[#121826] rounded-2xl border border-pink-500/20 p-8 shadow-2xl shadow-pink-500/5">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-gradient-to-br from-pink-500/25 to-purple-600/25 flex items-center justify-center border border-pink-500/30">
              <Lock className="w-7 h-7 text-pink-400" />
            </div>
            <p className="text-xs font-semibold uppercase tracking-widest text-pink-400/90 mb-1">Loranrix.com</p>
            <h1 className="text-2xl font-bold text-white mb-1">Administration</h1>
            <p className="text-gray-400 text-sm">Portfolio · CrushShy · contenus</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="on">
            <div>
              <label htmlFor="admin-username" className="block text-sm font-medium text-gray-300 mb-2">
                Identifiant
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  id="admin-username"
                  name="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                  className="w-full pl-11 pr-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-pink-500/50 focus:ring-1 focus:ring-pink-500/30"
                  placeholder="loran"
                />
              </div>
            </div>

            <div>
              <label htmlFor="admin-password" className="block text-sm font-medium text-gray-300 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  id="admin-password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full pl-11 pr-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-pink-500/50 focus:ring-1 focus:ring-pink-500/30"
                  placeholder="••••••••••••"
                />
              </div>
            </div>

            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: loading ? 1 : 1.01 }}
              whileTap={{ scale: loading ? 1 : 0.99 }}
              className="w-full py-3.5 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Se connecter
                </>
              )}
            </motion.button>
          </form>

          <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
            <Link
              to="/admin/crushshy/dashboard"
              className="flex items-center justify-center gap-2 text-sm text-pink-300/90 hover:text-pink-200"
            >
              <Heart className="w-4 h-4" fill="currentColor" />
              Accès direct CrushShy (après connexion)
            </Link>
            <Link
              to="/"
              className="flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-300"
            >
              <ExternalLink className="w-4 h-4" />
              Retour au site public
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
